import { CampaignDetails } from "@/lib/types";
import { showSuccess, showApiError } from "@/lib/toast";
import {
  getCampaignDetailItems,
  getCampaignDetailContent,
  addCampaignDetailContent,
  deleteCampaignDetailContent,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { CampaignContentListMetaForm, FormSchemaType } from "./CampaignContentListMetaForm";

type Props = {
  campaignDetails: CampaignDetails;
};

export const CampaignUpdateContentList: React.FC<Props> = ({ campaignDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const campaignDetailItemsShit = useQuery({
    queryKey: ["getCampaignDetailContent", campaignDetails.id],
    queryFn: () => getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid)),
  });
  const campaignDetailItemsDataShit = campaignDetailItemsShit.data?.data.result || [];
  const campaignDetailItemShit = campaignDetailItemsDataShit[0];

  const campaignDetailItems = useQuery({
    queryKey: ["getCampaignDetailItems", campaignDetails.intId],
    queryFn: () => getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid)),
    enabled: Boolean(campaignDetailItemShit?.id),
  });

  const campaignDetailItemsData = campaignDetailItems.data?.data.result || [];

  const { data: campaignDetailContent, isLoading: isCampaignDetailContentLoading } = useQuery({
    queryFn: () => getCampaignDetailContent(String(campaignDetailItemShit?.id), String(activeFirm?.firmGuid)),
    queryKey: ["getCampaignDetailContent", activeFirm?.firmGuid, campaignDetailItemShit?.id],
    enabled: Boolean(campaignDetailItemShit?.id),
  });
  const campaignDetailContentData = campaignDetailContent?.data.result || [];

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      const campaignDetailItem = campaignDetailItemsData.find((r) => r.id === campaignDetailItemShit?.id);
      if (!campaignDetailItem) return Promise.reject();

      for (const existing of campaignDetailContentData) {
        await deleteCampaignDetailContent(existing.id, String(activeFirm?.firmGuid));
      }

      for (const content of values.contentList) {
        if (content.contentId && campaignDetailItem.intId) {
          await addCampaignDetailContent({
            campaignDetailId: campaignDetailItem.intId,
            contentId: content.contentId || undefined,
            firmGuid: String(activeFirm?.firmGuid),
          });
        }
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailItems"] });
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailContent"] });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (isCampaignDetailContentLoading || !campaignDetailItemShit) return <LoadingSmall />;

  return (
    <CampaignContentListMetaForm
      buttonText={t("common.save")}
      onSubmit={(values) => updateMutation.mutate(values)}
      isLoading={updateMutation.isPending}
      defaultValues={{
        contentList: campaignDetailContentData.map((r) => ({
          contentId: r.contentintId,
          id: r.id,
        })),
      }}
    />
  );
};
